import { LIBRARY_FILE_ALLOWED_MIME_TYPES } from "@/data/use-library-files-data";
import {
  fileControllerCreateFile,
  libraryFileControllerConfirmFileUpload,
} from "@/lib/gen-api";
import { queryKeys } from "@/lib/query-keys";
import { contentTypeForUploadName, MAX_FILESIZE } from "@nnr/global";
import { useQueryClient } from "@tanstack/react-query";

export function useLibraryFileUploadAction(
  groupUuid: string | null,
  kitUuid: string | null,
) {
  const queryClient = useQueryClient();

  const upload = async ({
    kitUuid: paramsKitUuid,
    file,
  }: {
    kitUuid: string;
    file: File;
  }): Promise<{ uuid: string }> => {
    const finalKitUuid = paramsKitUuid || kitUuid;
    if (!groupUuid || !finalKitUuid) {
      throw new Error("Group UUID is required");
    }

    const isLocal = window.location.host === "local-dev.99ravens.ai";

    if (!LIBRARY_FILE_ALLOWED_MIME_TYPES.has(file.type)) {
      throw new Error(`File type "${file.type}" is not supported`);
    }
    if (file.size > MAX_FILESIZE) {
      throw new Error(
        `File size exceeds the ${MAX_FILESIZE / (1024 * 1024)} MB limit`,
      );
    }

    const createResponse = await fileControllerCreateFile({
      body: {
        name: file.name,
        isLibrary: true,
        isKnowledge: false,
        groupUuid: groupUuid!,
        kitUuid: finalKitUuid,
      },
    });
    const record = createResponse.data;
    if (!record) throw new Error("Failed to create library file record");

    const contentType = contentTypeForUploadName(record.name);
    if (!contentType) {
      throw new Error("Invalid or unsupported file name.");
    }

    const uploadResponse = await fetch(record.signedUrl, {
      method: isLocal ? "POST" : "PUT",
      body: file,
      headers: {
        "Content-Type": contentType,
        ...(isLocal
          ? {}
          : { "x-goog-content-length-range": `0,${MAX_FILESIZE}` }),
      },
    });
    if (!uploadResponse.ok) {
      throw new Error(`Failed to upload file: ${uploadResponse.statusText}`);
    }

    await libraryFileControllerConfirmFileUpload({
      path: {
        fileUuid: record.uuid,
      },
      throwOnError: true,
    });

    queryClient.invalidateQueries({
      queryKey: queryKeys.files({
        groupUuid: groupUuid!,
        kitUuid: finalKitUuid!,
        isLibrary: true,
      }),
    });

    return { uuid: record.uuid };
  };

  /**
   * Browse user computer files and the file buffer.
   */
  const browse = ({
    allowedMimeTypes,
    onSelect,
  }: {
    allowedMimeTypes: string[];
    onSelect: (file: File) => void;
  }) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = Array.from(allowedMimeTypes).join(",");
    input.style.display = "none";
    document.body.appendChild(input);
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) onSelect(file);
      input.remove();
    };
    input.addEventListener("cancel", () => {
      input.remove();
    });
    input.click();
  };

  const browseAndUpload = ({
    allowedMimeTypes,
    onSelect,
    onUpload,
  }: {
    allowedMimeTypes: string[];
    onSelect?: (file: File) => void;
    onUpload?: ({ file, uuid }: { file: File; uuid: string }) => void;
  }) => {
    if (!kitUuid) {
      throw new Error("Kit UUID is required");
    }

    browse({
      allowedMimeTypes,
      onSelect: async (file) => {
        onSelect?.(file);
        const { uuid } = await upload({ kitUuid, file });
        onUpload?.({ file, uuid });
      },
    });
  };

  return { upload, browse, browseAndUpload };
}
